package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

private data class SystemModuleItem(
    val id: String,
    val title: String,
    val description: String,
    val route: String,
    val icon: ImageVector,
    val iconBgColor: Color,
    val iconTintColor: Color,
    val badgeText: String? = null,
    val badgeBgColor: Color = Color(0xFFF1F5F9),
    val badgeTextColor: Color = Color(0xFF334155),
    val isFullWidth: Boolean = false,
    val chips: List<String> = emptyList()
)

@Composable
fun AdminDashboardScreen(
    onNavigate: (String) -> Unit = {}
) {
    val viewModel = koinViewModel<AdminViewModel>()
    val profile by viewModel.profile.collectAsState()
    val roomCount by viewModel.roomCount.collectAsState()
    val activeChannels by viewModel.activeChannels.collectAsState()
    val auditLogs by viewModel.auditLogs.collectAsState()
    val diagnostics by viewModel.diagnostics.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadDashboardData()
    }

    var searchQuery by remember { mutableStateOf("") }
    val mainScrollState = rememberScrollState()

    val isProfileApproved = !profile?.companyTitle.isNullOrBlank() && !profile?.taxNumber.isNullOrBlank()

    val strRoom = l10n("Oda")
    val strActiveChannels = l10n("Kanal Aktif")
    val strNoConnection = l10n("Bağlantı Yok")
    val strVerified = l10n("Onaylı")
    val strDraft = l10n("Taslak")

    // Module definitions dynamically derived from state
    val hotelConfigModules = remember(roomCount, isProfileApproved, strRoom, strVerified, strDraft) {
        listOf(
            SystemModuleItem(
                id = "hotel-profile",
                title = "Otel Profili",
                description = "Kurumsal unvan, iletişim, vergi ve fatura bilgileri",
                route = "HotelProfileScreen",
                icon = Icons.Default.Apartment,
                iconBgColor = Color(0xFFEFF6FF),
                iconTintColor = Color(0xFF2563EB),
                badgeText = if (isProfileApproved) strVerified else strDraft,
                badgeBgColor = if (isProfileApproved) Color(0xFFECFDF5) else Color(0xFFFFFBEB),
                badgeTextColor = if (isProfileApproved) Color(0xFF047857) else Color(0xFFB45309)
            ),
            SystemModuleItem(
                id = "room-management",
                title = "Oda Yönetimi",
                description = "Oda numaraları, kat tanımları ve oda tipleri",
                route = "RoomManagementScreen",
                icon = Icons.Default.MeetingRoom,
                iconBgColor = Color(0xFFF0FDFA),
                iconTintColor = Color(0xFF0D9488),
                badgeText = "$roomCount $strRoom",
                badgeBgColor = Color(0xFFF1F5F9),
                badgeTextColor = Color(0xFF334155)
            ),
            SystemModuleItem(
                id = "pricing-periods",
                title = "Fiyat Periyotları & Tarifeler",
                description = "Sezonluk fiyatlar, hafta sonu farkları ve oda tarifeleri",
                route = "PricingManagementScreen",
                icon = Icons.Default.Sell,
                iconBgColor = Color(0xFFFFFBEB),
                iconTintColor = Color(0xFFD97706),
                badgeText = "Dinamik",
                badgeBgColor = Color(0xFFFFFBEB),
                badgeTextColor = Color(0xFFB45309)
            ),
            SystemModuleItem(
                id = "floor-planner",
                title = "Kat Planı & Kroki Düzenleyici",
                description = "Kat krokileri, asansör, merdiven ve oda yerleşimleri",
                route = "FloorPlanEditorScreen",
                icon = Icons.Default.Layers,
                iconBgColor = Color(0xFFEEF2FF),
                iconTintColor = Color(0xFF4F46E5),
                badgeText = "İnteraktif",
                badgeBgColor = Color(0xFFEEF2FF),
                badgeTextColor = Color(0xFF4338CA)
            )
        )
    }

    val salesPartnerModules = remember(activeChannels, strActiveChannels, strNoConnection) {
        listOf(
            SystemModuleItem(
                id = "restaurant-pos",
                title = "Restoran & Menü Yönetimi",
                description = "Adisyon menüsü, ürün kategorileri ve oda servisi fiyatları",
                route = "AdminMenuManagementScreen",
                icon = Icons.Default.RestaurantMenu,
                iconBgColor = Color(0xFFFFF1F2),
                iconTintColor = Color(0xFFE11D48)
            ),
            SystemModuleItem(
                id = "agency-management",
                title = "Acenta Yönetimi",
                description = "Acente kartları, komisyon oranları ve acente kayıtları",
                route = "AdminAgencyManagementScreen",
                icon = Icons.Default.BusinessCenter,
                iconBgColor = Color(0xFFF0F9FF),
                iconTintColor = Color(0xFF0284C7)
            ),
            SystemModuleItem(
                id = "contingent-contracts",
                title = "Acente Kontenjan Sözleşmesi",
                description = "Acentelere özel oda blokajları ve dönemsel sözleşmeler",
                route = "AgencyContractScreen",
                icon = Icons.Default.Description,
                iconBgColor = Color(0xFFFAF5FF),
                iconTintColor = Color(0xFF9333EA)
            ),
            SystemModuleItem(
                id = "suppliers",
                title = "Tedarikçi Yönetimi",
                description = "Gıda, temizlik ve teknik malzeme tedarikçi kayıtları",
                route = "AdminSupplierManagementScreen",
                icon = Icons.Default.LocalShipping,
                iconBgColor = Color(0xFFEFF6FF),
                iconTintColor = Color(0xFF2563EB)
            ),
            SystemModuleItem(
                id = "channel-manager",
                title = "Channel Manager (OTA Senkronizasyonu)",
                description = "Booking.com, Expedia ve Airbnb çift yönlü anlık fiyat & kontenjan senkronizasyonu",
                route = "ChannelManagerScreen",
                icon = Icons.Default.Sync,
                iconBgColor = Color(0xFF059669),
                iconTintColor = Color.White,
                badgeText = if (activeChannels.isNotEmpty()) "${activeChannels.size} $strActiveChannels" else strNoConnection,
                badgeBgColor = if (activeChannels.isNotEmpty()) Color(0xFFD1FAE5) else Color(0xFFF1F5F9),
                badgeTextColor = if (activeChannels.isNotEmpty()) Color(0xFF065F46) else Color(0xFF64748B),
                isFullWidth = true,
                chips = activeChannels
            )
        )
    }

    val hrSecurityModules = remember(profile?.kbsFacilityCode) {
        listOf(
            SystemModuleItem(
                id = "departments",
                title = "Departman Yönetimi",
                description = "Ön büro, kat hizmetleri, muhasebe ve mutfak hiyerarşisi",
                route = "AdminDepartmentManagementScreen",
                icon = Icons.Default.CorporateFare,
                iconBgColor = Color(0xFFF1F5F9),
                iconTintColor = Color(0xFF334155)
            ),
            SystemModuleItem(
                id = "staff-roles",
                title = "Personel & Yetki Yönetimi",
                description = "Personel hesapları, sistem rolleri ve erişim izinleri",
                route = "AdminStaffManagementScreen",
                icon = Icons.Default.Badge,
                iconBgColor = Color(0xFFF0FDFA),
                iconTintColor = Color(0xFF0D9488)
            ),
            SystemModuleItem(
                id = "membership-restrictions",
                title = "Üye Yönetimi & Kısıtlama",
                description = "Üye listesi, üyelik kodları ve anlık aktif/pasif kısıtlama",
                route = "AdminMembersScreen",
                icon = Icons.Default.SupervisedUserCircle,
                iconBgColor = Color(0xFFFFFBEB),
                iconTintColor = Color(0xFFD97706),
                badgeText = "Güvenlik",
                badgeBgColor = Color(0xFFFFFBEB),
                badgeTextColor = Color(0xFFB45309)
            ),
            SystemModuleItem(
                id = "blacklist",
                title = "Müşteri Kara Liste Yönetimi",
                description = "Sorunlu misafir kayıtları ve erken rezervasyon engelleme",
                route = "AdminBlacklistScreen",
                icon = Icons.Default.Block,
                iconBgColor = Color(0xFFFFF1F2),
                iconTintColor = Color(0xFFE11D48),
                badgeText = "Risk",
                badgeBgColor = Color(0xFFFFF1F2),
                badgeTextColor = Color(0xFFBE123C)
            ),
            SystemModuleItem(
                id = "kbs-settings",
                title = "KBS Kimlik Bildirim Ayarları",
                description = "EGM / AKBS & Jandarma tesis kodu, şifre ve hibrit bildirim entegrasyonu",
                route = "KbsSettingsScreen",
                icon = Icons.Default.Security,
                iconBgColor = Color(0xFFEFF6FF),
                iconTintColor = Color(0xFF1E3A8A),
                badgeText = if (!profile?.kbsFacilityCode.isNullOrBlank()) "Aktif" else "Yapılandırılmadı",
                badgeBgColor = if (!profile?.kbsFacilityCode.isNullOrBlank()) Color(0xFFECFDF5) else Color(0xFFFFFBEB),
                badgeTextColor = if (!profile?.kbsFacilityCode.isNullOrBlank()) Color(0xFF047857) else Color(0xFFB45309)
            ),
            SystemModuleItem(
                id = "advanced-reports",
                title = "Gelişmiş Raporlar & İstatistikler",
                description = "Doluluk trendleri, departman kârlılığı, RevPAR ve yönetici maliyet özetleri",
                route = "StatisticsScreen",
                icon = Icons.Default.BarChart,
                iconBgColor = Color(0xFFECFDF5),
                iconTintColor = Color(0xFF059669),
                isFullWidth = true
            )
        )
    }

    fun matchesSearch(item: SystemModuleItem): Boolean {
        if (searchQuery.isBlank()) return true
        val query = searchQuery.trim().lowercase()
        return item.title.lowercase().contains(query) ||
               item.description.lowercase().contains(query) ||
               (item.badgeText?.lowercase()?.contains(query) == true) ||
               item.chips.any { it.lowercase().contains(query) }
    }

    val filteredHotelConfig = hotelConfigModules.filter { matchesSearch(it) }
    val filteredSalesPartners = salesPartnerModules.filter { matchesSearch(it) }
    val filteredHrSecurity = hrSecurityModules.filter { matchesSearch(it) }

    val hotelName = profile?.name?.takeIf { it.isNotBlank() }
        ?: profile?.companyTitle?.takeIf { it.isNotBlank() }
        ?: l10n("Tesis Yönetimi")

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF8FAFC))
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val isDesktop = maxWidth >= 1050.dp

            if (isDesktop) {
                // Desktop: Two-Column Workspace Layout (Main Content 72% + Right Dock 28%)
                Row(modifier = Modifier.fillMaxSize()) {
                    // Left Main Panel
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight()
                            .verticalScroll(mainScrollState)
                            .verticalScrollbar(mainScrollState)
                            .padding(horizontal = 32.dp, vertical = 24.dp),
                        verticalArrangement = Arrangement.spacedBy(32.dp)
                    ) {
                        AdminHeaderSection(
                            searchQuery = searchQuery,
                            onSearchQueryChange = { searchQuery = it },
                            onBackClick = { onNavigate("back") },
                            hotelName = hotelName
                        )

                        if (filteredHotelConfig.isNotEmpty()) {
                            CategorySection(
                                categoryTitle = l10n("Otel & Konaklama Yapılandırması"),
                                categorySubtitle = l10n("Otel bilgileri, oda tipleri, envanter ve fiyat tarifeleri"),
                                dotColor = Color(0xFF2563EB),
                                countText = "${filteredHotelConfig.size} " + l10n("Modül"),
                                modules = filteredHotelConfig,
                                onNavigate = onNavigate
                            )
                        }

                        if (filteredSalesPartners.isNotEmpty()) {
                            CategorySection(
                                categoryTitle = l10n("Satış, Menü & İş Ortakları"),
                                categorySubtitle = l10n("Restoran menüsü, acenteler, sözleşmeler ve kanal yönetimi"),
                                dotColor = Color(0xFFF97316),
                                countText = "${filteredSalesPartners.size} " + l10n("Modül"),
                                modules = filteredSalesPartners,
                                onNavigate = onNavigate
                            )
                        }

                        if (filteredHrSecurity.isNotEmpty()) {
                            CategorySection(
                                categoryTitle = l10n("İK, Güvenlik & Sistem Raporları"),
                                categorySubtitle = l10n("Departmanlar, personel yetkileri, güvenlik ve genel istatistikler"),
                                dotColor = Color(0xFF9333EA),
                                countText = "${filteredHrSecurity.size} " + l10n("Modül"),
                                modules = filteredHrSecurity,
                                onNavigate = onNavigate
                            )
                        }

                        Spacer(modifier = Modifier.height(24.dp))
                    }

                    // Vertical Divider
                    Box(
                        modifier = Modifier
                            .fillMaxHeight()
                            .width(1.dp)
                            .background(Color(0xFFE2E8F0))
                    )

                    // Right Diagnostics & Quick Operations Sidebar
                    val rightScrollState = rememberScrollState()
                    Column(
                        modifier = Modifier
                            .width(340.dp)
                            .fillMaxHeight()
                            .background(Color.White)
                            .verticalScroll(rightScrollState)
                            .verticalScrollbar(rightScrollState)
                            .padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(24.dp)
                    ) {
                        SystemDiagnosticsWidget(diagnostics = diagnostics)
                        QuickActionsWidget(
                            onNavigate = onNavigate,
                            onForceRefresh = { viewModel.loadDashboardData() }
                        )
                        AuditFeedWidget(
                            auditLogs = auditLogs,
                            onViewAll = { onNavigate("audit-log") }
                        )
                        KmpComplianceWidget()
                    }
                }
            } else {
                // Mobile / Tablet: Single Column Responsive Layout
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(mainScrollState)
                        .verticalScrollbar(mainScrollState)
                        .padding(horizontal = 16.dp, vertical = 20.dp),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    AdminHeaderSection(
                        searchQuery = searchQuery,
                        onSearchQueryChange = { searchQuery = it },
                        onBackClick = { onNavigate("back") },
                        hotelName = hotelName
                    )

                    if (filteredHotelConfig.isNotEmpty()) {
                        CategorySection(
                            categoryTitle = l10n("Otel & Konaklama Yapılandırması"),
                            categorySubtitle = l10n("Otel bilgileri, oda tipleri, envanter ve fiyat tarifeleri"),
                            dotColor = Color(0xFF2563EB),
                            countText = "${filteredHotelConfig.size} " + l10n("Modül"),
                            modules = filteredHotelConfig,
                            onNavigate = onNavigate
                        )
                    }

                    if (filteredSalesPartners.isNotEmpty()) {
                        CategorySection(
                            categoryTitle = l10n("Satış, Menü & İş Ortakları"),
                            categorySubtitle = l10n("Restoran menüsü, acenteler, sözleşmeler ve kanal yönetimi"),
                            dotColor = Color(0xFFF97316),
                            countText = "${filteredSalesPartners.size} " + l10n("Modül"),
                            modules = filteredSalesPartners,
                            onNavigate = onNavigate
                        )
                    }

                    if (filteredHrSecurity.isNotEmpty()) {
                        CategorySection(
                            categoryTitle = l10n("İK, Güvenlik & Sistem Raporları"),
                            categorySubtitle = l10n("Departmanlar, personel yetkileri, güvenlik ve genel istatistikler"),
                            dotColor = Color(0xFF9333EA),
                            countText = "${filteredHrSecurity.size} " + l10n("Modül"),
                            modules = filteredHrSecurity,
                            onNavigate = onNavigate
                        )
                    }

                    HorizontalDivider(color = Color(0xFFE2E8F0))

                    // Embedded Diagnostics & Actions on smaller screens
                    SystemDiagnosticsWidget(diagnostics = diagnostics)
                    QuickActionsWidget(
                        onNavigate = onNavigate,
                        onForceRefresh = { viewModel.loadDashboardData() }
                    )
                    AuditFeedWidget(
                        auditLogs = auditLogs,
                        onViewAll = { onNavigate("audit-log") }
                    )
                    KmpComplianceWidget()

                    Spacer(modifier = Modifier.height(24.dp))
                }
            }
        }
    }
}

@Composable
private fun AdminHeaderSection(
    searchQuery: String,
    onSearchQueryChange: (String) -> Unit,
    onBackClick: () -> Unit,
    hotelName: String
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Top breadcrumbs & Live badge row
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Surface(
                    onClick = onBackClick,
                    shape = RoundedCornerShape(8.dp),
                    color = Color.White,
                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.size(36.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Geri",
                            tint = Color(0xFF475569),
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }

                Column {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Text(
                            text = "HotelOS",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                            color = Color(0xFF94A3B8)
                        )
                        Text(
                            text = "/",
                            fontSize = 12.sp,
                            color = Color(0xFFCBD5E1)
                        )
                        Text(
                            text = l10n("Sistem Yönetimi"),
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = Color(0xFF1D4ED8)
                        )
                    }
                    Text(
                        text = l10n("Sistem Yönetim Paneli"),
                        fontSize = 20.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0F172A),
                        letterSpacing = (-0.5).sp
                    )
                }
            }

            // Live status badge
            Surface(
                shape = RoundedCornerShape(6.dp),
                color = Color(0xFFECFDF5),
                border = BorderStroke(1.dp, Color(0xFFA7F3D0))
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(7.dp)
                            .clip(CircleShape)
                            .background(Color(0xFF10B981))
                    )
                    Text(
                        text = l10n("Canlı Ortam"),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color(0xFF047857)
                    )
                }
            }
        }

        // Search Input
        Surface(
            shape = RoundedCornerShape(10.dp),
            color = Color.White,
            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
            modifier = Modifier
                .fillMaxWidth()
                .height(44.dp)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Search,
                    contentDescription = null,
                    tint = Color(0xFF94A3B8),
                    modifier = Modifier.size(18.dp)
                )
                BasicTextField(
                    value = searchQuery,
                    onValueChange = onSearchQueryChange,
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    textStyle = TextStyle(
                        fontSize = 13.sp,
                        color = Color(0xFF1E293B),
                        fontWeight = FontWeight.Normal
                    ),
                    cursorBrush = SolidColor(Color(0xFF2563EB)),
                    decorationBox = { innerTextField ->
                        if (searchQuery.isEmpty()) {
                            Text(
                                text = l10n("Modül veya ayar ara (Ctrl+K)..."),
                                fontSize = 13.sp,
                                color = Color(0xFF94A3B8)
                            )
                        }
                        innerTextField()
                    }
                )
                if (searchQuery.isNotEmpty()) {
                    IconButton(
                        onClick = { onSearchQueryChange("") },
                        modifier = Modifier.size(24.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Clear,
                            contentDescription = "Temizle",
                            tint = Color(0xFF94A3B8),
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CategorySection(
    categoryTitle: String,
    categorySubtitle: String,
    dotColor: Color,
    countText: String,
    modules: List<SystemModuleItem>,
    onNavigate: (String) -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        // Category Header
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(8.dp)
                            .clip(CircleShape)
                            .background(dotColor)
                    )
                    Text(
                        text = categoryTitle.uppercase(),
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0F172A),
                        letterSpacing = 0.5.sp
                    )
                }
                Text(
                    text = categorySubtitle,
                    fontSize = 12.sp,
                    color = Color(0xFF64748B),
                    modifier = Modifier.padding(start = 16.dp, top = 2.dp)
                )
            }
            Text(
                text = countText,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                color = Color(0xFF94A3B8)
            )
        }

        // Cards layout
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val isSingleColumn = maxWidth < 640.dp

            if (isSingleColumn) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    modules.forEach { module ->
                        ModuleCard(module = module, onClick = { onNavigate(module.route) })
                    }
                }
            } else {
                // Two-column layout with support for full-width cards
                val regularModules = modules.filter { !it.isFullWidth }
                val fullWidthModules = modules.filter { it.isFullWidth }

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    regularModules.chunked(2).forEach { rowModules ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(14.dp)
                        ) {
                            rowModules.forEach { module ->
                                Box(modifier = Modifier.weight(1f)) {
                                    ModuleCard(module = module, onClick = { onNavigate(module.route) })
                                }
                            }
                            if (rowModules.size == 1) {
                                Spacer(modifier = Modifier.weight(1f))
                            }
                        }
                    }

                    fullWidthModules.forEach { module ->
                        ModuleCard(module = module, onClick = { onNavigate(module.route) })
                    }
                }
            }
        }
    }
}

@Composable
private fun ModuleCard(
    module: SystemModuleItem,
    onClick: () -> Unit
) {
    val isChannelManager = module.id == "channel-manager"
    val cardBorder = if (isChannelManager) {
        BorderStroke(1.dp, Color(0xFF6EE7B7))
    } else {
        BorderStroke(1.dp, Color(0xFFE2E8F0))
    }
    val cardBg = if (isChannelManager) {
        Color(0xFFF0FDF4)
    } else {
        Color.White
    }

    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(14.dp),
        color = cardBg,
        border = cardBorder,
        shadowElevation = 0.dp,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                // Icon Box
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(module.iconBgColor),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = module.icon,
                        contentDescription = module.title,
                        tint = module.iconTintColor,
                        modifier = Modifier.size(22.dp)
                    )
                }

                // Text details
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(3.dp)
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = l10n(module.title),
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        module.badgeText?.let { badge ->
                            Surface(
                                shape = RoundedCornerShape(4.dp),
                                color = module.badgeBgColor
                            ) {
                                Text(
                                    text = l10n(badge),
                                    fontSize = 10.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = module.badgeTextColor,
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                )
                            }
                        }
                    }

                    Text(
                        text = l10n(module.description),
                        fontSize = 12.sp,
                        color = Color(0xFF64748B),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    // Optional Chips (e.g. for Channel Manager: Booking, Expedia, Airbnb)
                    if (module.chips.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(2.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            module.chips.forEach { chip ->
                                Surface(
                                    shape = RoundedCornerShape(4.dp),
                                    color = Color(0xFFF1F5F9),
                                    border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                                ) {
                                    Text(
                                        text = chip,
                                        fontSize = 10.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Color(0xFF475569),
                                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.width(8.dp))

            // Action Indicator Arrow
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(CircleShape)
                    .background(Color(0xFFF8FAFC)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                    contentDescription = "Detay",
                    tint = Color(0xFF94A3B8),
                    modifier = Modifier.size(16.dp)
                )
            }
        }
    }
}

@Composable
private fun SystemDiagnosticsWidget(diagnostics: SystemDiagnosticsData) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color(0xFFF8FAFC),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(7.dp)
                            .clip(CircleShape)
                            .background(if (diagnostics.isOnline) Color(0xFF10B981) else Color(0xFFEF4444))
                    )
                    Text(
                        text = l10n("Sistem Durumu"),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0F172A),
                        letterSpacing = 0.4.sp
                    )
                }
                Surface(
                    shape = RoundedCornerShape(12.dp),
                    color = if (diagnostics.isOnline) Color(0xFFD1FAE5) else Color(0xFFFEE2E2)
                ) {
                    Text(
                        text = if (diagnostics.isOnline) l10n("Çevrimiçi") else l10n("Bağlantı Yok"),
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold,
                        color = if (diagnostics.isOnline) Color(0xFF065F46) else Color(0xFFB91C1C),
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                    )
                }
            }

            HorizontalDivider(color = Color(0xFFE2E8F0))

            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                DiagnosticItem(
                    label = "KMP Cloud Gateway",
                    value = if (diagnostics.latencyMs != null) "${diagnostics.latencyMs} ms" else l10n("Bekleniyor"),
                    valueColor = if (diagnostics.isOnline) Color(0xFF059669) else Color(0xFFEF4444),
                    hasDot = true
                )
                DiagnosticItem(
                    label = "OTA Senkronizasyonu",
                    value = diagnostics.lastSyncText.ifBlank { l10n("Beklemede") }
                )
                DiagnosticItem(
                    label = "Tesis Veritabanı",
                    value = diagnostics.databaseStatus
                )
                DiagnosticItem(
                    label = "Yazılım Lisansı",
                    value = diagnostics.licenseStatus,
                    valueColor = Color(0xFF1D4ED8),
                    isBold = true
                )
            }
        }
    }
}

@Composable
private fun DiagnosticItem(
    label: String,
    value: String,
    valueColor: Color = Color(0xFF0F172A),
    hasDot: Boolean = false,
    isBold: Boolean = false
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = l10n(label),
            fontSize = 11.sp,
            color = Color(0xFF64748B)
        )
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            if (hasDot) {
                Box(
                    modifier = Modifier
                        .size(5.dp)
                        .clip(CircleShape)
                        .background(valueColor)
                )
            }
            Text(
                text = value,
                fontSize = 11.sp,
                fontWeight = if (isBold) FontWeight.Bold else FontWeight.Medium,
                color = valueColor
            )
        }
    }
}

@Composable
private fun QuickActionsWidget(
    onNavigate: (String) -> Unit,
    onForceRefresh: () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text(
            text = l10n("Hızlı Sistem Eylemleri").uppercase(),
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            color = Color(0xFF0F172A),
            letterSpacing = 0.4.sp
        )

        Button(
            onClick = { onNavigate("RoomManagementScreen") },
            shape = RoundedCornerShape(8.dp),
            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1D4ED8)),
            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 10.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                    Text(
                        text = l10n("Yeni Oda / Tip Ekle"),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold
                    )
                }
                Surface(
                    shape = RoundedCornerShape(4.dp),
                    color = Color(0xFF1E40AF)
                ) {
                    Text(
                        text = "Alt+N",
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Medium,
                        color = Color.White,
                        modifier = Modifier.padding(horizontal = 5.dp, vertical = 2.dp)
                    )
                }
            }
        }

        OutlinedButton(
            onClick = {
                onForceRefresh()
                onNavigate("ChannelManagerScreen")
            },
            shape = RoundedCornerShape(8.dp),
            border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
            colors = ButtonDefaults.outlinedButtonColors(contentColor = Color(0xFF334155)),
            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 10.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Sync,
                    contentDescription = null,
                    tint = Color(0xFF64748B),
                    modifier = Modifier.size(16.dp)
                )
                Text(
                    text = l10n("Tüm OTA Kanallarını Zorla Yenile"),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                )
            }
        }

        OutlinedButton(
            onClick = { onNavigate("PricingManagementScreen") },
            shape = RoundedCornerShape(8.dp),
            border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
            colors = ButtonDefaults.outlinedButtonColors(contentColor = Color(0xFF334155)),
            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 10.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Tune,
                    contentDescription = null,
                    tint = Color(0xFF64748B),
                    modifier = Modifier.size(16.dp)
                )
                Text(
                    text = l10n("Fiyat Güncelleme Sihirbazı"),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                )
            }
        }
    }
}

@Composable
private fun AuditFeedWidget(
    auditLogs: List<SystemAuditLog>,
    onViewAll: () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = l10n("Son Sistem Günlükleri").uppercase(),
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF0F172A),
                letterSpacing = 0.4.sp
            )
            Text(
                text = l10n("Tümü"),
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                color = Color(0xFF2563EB),
                modifier = Modifier.clickable { onViewAll() }
            )
        }

        if (auditLogs.isEmpty()) {
            Surface(
                shape = RoundedCornerShape(8.dp),
                color = Color(0xFFF8FAFC),
                border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Info,
                        contentDescription = null,
                        tint = Color(0xFF94A3B8),
                        modifier = Modifier.size(16.dp)
                    )
                    Text(
                        text = l10n("Henüz kayıtlı işlem günlüğü bulunmuyor."),
                        fontSize = 11.sp,
                        color = Color(0xFF64748B)
                    )
                }
            }
        } else {
            auditLogs.forEachIndexed { index, log ->
                val dotColor = when (index % 3) {
                    0 -> Color(0xFF2563EB)
                    1 -> Color(0xFFF59E0B)
                    else -> Color(0xFF10B981)
                }
                AuditLogItem(
                    dotColor = dotColor,
                    title = log.title,
                    description = log.description,
                    meta = if (log.author.isNotBlank()) "${log.timeAgo} • ${log.author}" else log.timeAgo
                )
            }
        }
    }
}

@Composable
private fun AuditLogItem(
    dotColor: Color,
    title: String,
    description: String,
    meta: String
) {
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = Color(0xFFF8FAFC),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Box(
                modifier = Modifier
                    .padding(top = 4.dp)
                    .size(6.dp)
                    .clip(CircleShape)
                    .background(dotColor)
            )
            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = l10n(title),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Color(0xFF1E293B)
                )
                Text(
                    text = l10n(description),
                    fontSize = 11.sp,
                    color = Color(0xFF64748B),
                    lineHeight = 14.sp
                )
                Text(
                    text = meta,
                    fontSize = 10.sp,
                    color = Color(0xFF94A3B8)
                )
            }
        }
    }
}

@Composable
private fun KmpComplianceWidget() {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color(0xFF0F172A),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(6.dp)
                        .clip(CircleShape)
                        .background(Color(0xFF34D399))
                )
                Text(
                    text = "KMP DESKTOP CLIENT",
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFFCBD5E1),
                    letterSpacing = 0.5.sp
                )
            }
            Text(
                text = l10n("Kotlin Multiplatform masaüstü motoru ve kurumsal şifreleme protokolleri aktif. Çift faktörlü doğrulama zorunludur."),
                fontSize = 11.sp,
                color = Color(0xFF94A3B8),
                lineHeight = 15.sp
            )
        }
    }
}




